import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from './base/base.service';

import { AppConfig } from './app-config';
import { PagedResponse, ObjectResponse, ListResponse } from './../interfaces/response';

import {
  FunctionalAnalysesData, FunctionalAnalysisDataPayload, Pba, Tba, UncontrolledTransaction, FunctionalAnalysisObjectTypeEnum,
  FunctionalAnalysisGlobalTemplate, SaveLibraryDataPayload, ImportFunctionalAnalysisGlobalTemplatePayload
} from '../models';

@Injectable()
export class FunctionalAnalysesService extends BaseService<FunctionalAnalysesData> {

  constructor(protected http: HttpClient, protected config: AppConfig) {
    super(http, config);
  }

  public getFunctionalAnalysesForArea(
    transactionId: number,
    areaId: number,
    typeId: number,
    queryString: string
  ): Observable<PagedResponse<FunctionalAnalysesData>> {
    const apiUrl =
      this.applyQueryString(queryString, `${this.getResourceUrl()}${transactionId}/area/${areaId}/type/${typeId}`);

    return this.http.get<PagedResponse<FunctionalAnalysesData>>(apiUrl);
  }

  public saveCharacteristic(
    transactionId: number,
    areaId: number,
    typeId: number,
    data: FunctionalAnalysesData
  ): Observable<ObjectResponse<FunctionalAnalysesData>> {
    const apiUrl = `${this.getResourceUrl()}${transactionId}/characteristic/area/${areaId}/type/${typeId}`;

    return this.http.post<ObjectResponse<FunctionalAnalysesData>>(apiUrl, data);
  }

  public initialize(transactionId: number, type: number): Observable<ListResponse<FunctionalAnalysesData>> {
    const apiUrl = `${this.getResourceUrl()}${transactionId}/initialize/type/${type}`;

    return this.http.post<ListResponse<FunctionalAnalysesData>>(apiUrl, {});
  }

  public reinitialize(transactionId: number, type: number, queryString: string): Observable<ListResponse<FunctionalAnalysesData>> {
    const apiUrl = this.applyQueryString(queryString, `${this.getResourceUrl()}${transactionId}/reinitialize/type/${type}`);

    return this.http.post<ListResponse<FunctionalAnalysesData>>(apiUrl, {});
  }

  public getCharacteristic(
    characteristicId: number,
    typeId: number
  ): Observable<ObjectResponse<FunctionalAnalysesData>> {
    const apiUrl = `${this.getResourceUrl()}characteristic/${characteristicId}/type/${typeId}`;

    return this.http.get<ObjectResponse<FunctionalAnalysesData>>(apiUrl);
  }

  public updateCharacteristic(
    characteristicId: number,
    typeId: number,
    payload: FunctionalAnalysisDataPayload
  ): Observable<ObjectResponse<FunctionalAnalysesData>> {
    const apiUrl = `${this.getResourceUrl()}characteristic/${characteristicId}/type/${typeId}`;
    return this.http.patch<ObjectResponse<FunctionalAnalysesData>>(apiUrl, payload);
  }

  public removeCharacteristic(
    characteristicId: number,
    typeId: number
  ): Observable<ObjectResponse<FunctionalAnalysesData>> {
    const apiUrl = `${this.getResourceUrl()}characteristic/${characteristicId}/type/${typeId}`;

    return this.http.delete<ObjectResponse<FunctionalAnalysesData>>(apiUrl);
  }

  public copyTransactionFunctionalAnalyses(
    targetTransactionId: number,
    sourceAnalysis: Pba | Tba | UncontrolledTransaction,
    type: number
  ): Observable<ListResponse<FunctionalAnalysesData>> {
    const data = this.formatAnalysis(sourceAnalysis);
    const apiUrl = `${this.getResourceUrl()}copy/${targetTransactionId}/type/${type}`;

    return this.http.put<ListResponse<FunctionalAnalysesData>>(apiUrl, data);
  }

  public formatAnalysis(analysis: Pba | Tba | UncontrolledTransaction): { sourceId: number, uncontrolled: boolean } {
    let analysisId: number = (analysis as Tba).tbaId;
    analysisId = analysisId || ((analysis as Pba).testedParty && (analysis as Pba).testedParty.testedPartyId);
    analysisId = analysisId || (analysis as UncontrolledTransaction).transactionId;

    return {
      sourceId: analysisId,
      uncontrolled: ((analysis as UncontrolledTransaction).transactionId === analysisId)
    };
  }

  public getAllGlobalTemplates(analysisType: FunctionalAnalysisObjectTypeEnum): Observable<ListResponse<FunctionalAnalysisGlobalTemplate>> {
    return this.http.get<ListResponse<FunctionalAnalysisGlobalTemplate>>(`${this.getResourceUrl()}global-library/analysis-type/${analysisType}`);
  }

  public createGlobalTemplate(analysisType: FunctionalAnalysisObjectTypeEnum, analysisId: number, payload: SaveLibraryDataPayload): Observable<Object> {
    return this.http.post(`${this.getResourceUrl()}global-library/analysis-type/${analysisType}/id/${analysisId}`, payload);
  }

  public importGlobalTemplate(analysisType: FunctionalAnalysisObjectTypeEnum, analysisId: number, payload: ImportFunctionalAnalysisGlobalTemplatePayload): Observable<Object> {
    return this.http.put(`${this.getResourceUrl()}global-library/analysis-type/${analysisType}/id/${analysisId}`, payload);
  }

  public deleteGlobalTemplate(analysisType: FunctionalAnalysisObjectTypeEnum, templateId: number): Observable<Object> {
    return this.http.delete(`${this.getResourceUrl()}global-library/analysis-type/${analysisType}/id/${templateId}`);
  }

  public templateAlreadyExists(analysisType: FunctionalAnalysisObjectTypeEnum, templateName: string): Observable<boolean> {
    return this.getAllGlobalTemplates(analysisType).pipe(
      map(({ data: templates }: ListResponse<FunctionalAnalysisGlobalTemplate>) => {
        return templates.some(template => template.templateName === templateName);
      })
    );
  }

  protected getResourceUrl(): string {
    return `${this.baseUrl}functional-analysis/`;
  }

}
